import {
  Component,
  Input,
  OnChanges,
  SimpleChanges,
} from '@angular/core';
import {
  AbstractControl,
  NG_VALIDATORS,
  NG_VALUE_ACCESSOR,
  ValidationErrors,
} from '@angular/forms';
import { CustomSelectOption } from 'src/app/core/interfaces/custom-select-option';
import { ParkingBay } from 'src/app/core/models/parking-bay';
import { ParkingBayService } from 'src/app/core/services/general/parking-bay.service';

@Component({
  selector: 'app-parking-bay-input',
  templateUrl: './parking-bay-input.component.html',
  styleUrls: ['./parking-bay-input.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      multi: true,
      useExisting: ParkingBayInputComponent,
    },
    {
      provide: NG_VALIDATORS,
      multi: true,
      useExisting: ParkingBayInputComponent,
    },
  ],
})
export class ParkingBayInputComponent implements OnChanges {
  @Input()
  title = "Select Parking Bay...";

  @Input()
  isDisabled = false;

  @Input()
  parkingBaySectionUuid!: string;

  public value?: string;
  public localValue?: CustomSelectOption;

  public parkingBaysList: Array<CustomSelectOption> = [];
  public parkingBays: Array<ParkingBay> = [];
  public loading = false;

  constructor(
    private parkingBayService: ParkingBayService,
  ) {
    // Don't load parking bays in constructor anymore
    // They will be loaded when parkingBaySectionUuid is provided
  }

  ngOnInit(): void {
    // Load parking bays if parkingBaySectionUuid is already available
    if (this.parkingBaySectionUuid) {
      this.loadParkingBaysForSection(this.parkingBaySectionUuid);
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    // React to changes in parkingBaySectionUuid
    if (changes['parkingBaySectionUuid']) {
      const currentValue = changes['parkingBaySectionUuid'].currentValue;
      const previousValue = changes['parkingBaySectionUuid'].previousValue;

      // If section changed, clear current selection and load new parking bays
      if (currentValue !== previousValue) {
        this.clearSelection();

        if (currentValue) {
          this.loadParkingBaysForSection(currentValue);
        } else {
          // Clear parking bays if no section selected
          this.clearParkingBays();
        }
      }
    }
  }

  private loadParkingBaysForSection(sectionUuid: string): void {
    if (!sectionUuid) {
      this.clearParkingBays();
      return;
    }

    this.loading = true;
    this.parkingBayService
      .allByParkingBaySection(sectionUuid)
      .subscribe({
        next: (res: Array<ParkingBay>) => {
          this.parkingBays = res.map((el) => new ParkingBay(el));
          this.parkingBaysList = res.map((el) => {
            return {
              name: `${el.parking_bay_number}`,
              value: el.uuid,
            }
          });
          this.writeValue(this.value);
          this.loading = false;
        },
        error: (error) => {
          console.error('Error loading parking bays for section:', error);
          this.clearParkingBays();
          this.loading = false;
        }
      });
  }

  private clearSelection(): void {
    this.value = undefined;
    this.localValue = undefined;
    this.onChange(undefined);
  }

  private clearParkingBays(): void {
    this.parkingBays = [];
    this.parkingBaysList = [];
  }

  onChange = (val: string | undefined) => {
  };

  onTouched = () => { };

  touched = false;
  disabled = false;

  writeValue(val?: string) {
    this.value = val;
    if (this.value != undefined || this.value != null) {
      this.localValue = this.parkingBaysList.filter((el) => el.value == val)[0];
    }
  }

  onValueChange(val?: CustomSelectOption) {
    this.value = val?.value;
    this.onChange(this.value);
    this.markAsTouched();
  }

  registerOnChange(onChange: any) {
    this.onChange = onChange;
  }

  registerOnTouched(onTouched: any) {
    this.onTouched = onTouched;
  }

  markAsTouched() {
    if (!this.touched) {
      this.onTouched();
      this.touched = true;
    }
  }

  setDisabledState(disabled: boolean) {
    this.disabled = disabled;
  }

  validate(control: AbstractControl): ValidationErrors | null {
    const value = control.value;

    if (!value) {
      return null;
    }

    return null;
  }


  getFilteredList(){
    // No need to filter anymore since we're only loading parking bays for the selected section
    return this.parkingBaysList;
  }

  getTitle(): string {
    if (!this.parkingBaySectionUuid) {
      return "Please select a parking bay section first";
    }
    if (this.loading) {
      return "Loading parking bays...";
    }
    if (this.parkingBaysList.length === 0) {
      return "No parking bays available for this section";
    }
    return this.title;
  }
}
